import { BENCHMARK_TOC_ITEMS, BENCHMARK_WIKI_ROOT } from './benchmark-catalog';
import { CORE_CODEX_SKILLS } from './codex-skill-catalog';

export interface AwesomeLlmAppEntry {
    id: string;
    title: string;
    category: string;
    summary: string;
    href: string;
    keywords: string[];
}

export interface AwesomeLlmAppBlock {
    label: string;
    title: string;
    text: string;
}

export interface AwesomeLlmAppSection {
    id: string;
    order: string;
    title: string;
    summary: string;
    description: string;
    related: string[];
    entries: AwesomeLlmAppEntry[];
    blocks: AwesomeLlmAppBlock[];
}

const entry = (
    id: string,
    title: string,
    category: string,
    summary: string,
    href: string,
    keywords: string[],
): AwesomeLlmAppEntry => ({
    id,
    title,
    category,
    summary,
    href,
    keywords,
});

const BENCHMARK_ENTRIES: AwesomeLlmAppEntry[] = [
    entry(
        'benchmark-hub',
        'LLM Agent Benchmarks',
        '성능·검증 > 벤치마크 허브',
        `최신 모델 그래프와 ${BENCHMARK_TOC_ITEMS.length}개 하위 벤치마크 상세 문서를 모은 내부 조사 허브입니다.`,
        '/benchmarks/llm-agent',
        ['benchmark', 'agent benchmark', 'model comparison', '성능', '검증', '벤치마크'],
    ),
    ...BENCHMARK_TOC_ITEMS.map((benchmark) => entry(
        `benchmark-${benchmark.slug}`,
        benchmark.name,
        `${BENCHMARK_WIKI_ROOT} > ${benchmark.category}`,
        benchmark.summary,
        `/benchmarks/llm-agent/${benchmark.slug}`,
        [
            benchmark.name,
            benchmark.badge,
            benchmark.category,
            benchmark.measures,
            benchmark.bestFor,
            '성능',
            '검증',
            '벤치마크',
        ],
    )),
];

const CODEX_SKILL_ENTRIES: AwesomeLlmAppEntry[] = CORE_CODEX_SKILLS.map((skill) => entry(
    `codex-skill-${skill.id}`,
    skill.title,
    `Codex Skills > ${skill.category}`,
    skill.summary,
    skill.href,
    [
        skill.title,
        skill.category,
        skill.status,
        ...skill.tags,
        'codex',
        'skill',
        '스킬',
    ],
));

const FRAMEWORK_ENTRIES: AwesomeLlmAppEntry[] = [
    entry(
        'caveman-framework',
        'Caveman',
        '에이전트 프레임워크 > 출력 압축',
        'Claude Code와 Codex 같은 코딩 에이전트의 응답 규칙을 압축형 커뮤니케이션 프로토콜로 묶어 토큰, 비용, 읽는 부담을 줄이는 LLM 프레임워크입니다. 내부 조사 상세 페이지로 이동합니다.',
        '/frameworks/caveman',
        [
            'caveman',
            'framework',
            'llm framework',
            'agent framework',
            'compressed communication',
            'token compression',
            'claude code',
            'codex',
            '원시인말투',
            '프레임워크',
            '토큰 절감',
            '비용 절감',
        ],
    ),
];

const TAXONOMY_ENTRIES: AwesomeLlmAppEntry[] = [
    entry(
        'llm-taxonomy-2026-06',
        'LLM 분류 체계: 2026년 6월',
        '모델 분류 > 운영 체계',
        'OpenAI, Anthropic, Google, DeepSeek, Qwen, Mistral, Meta와 AI Index/Safety Report를 기준으로 LLM을 제공 방식, 능력 축, 실행 표면, 컨텍스트, 검증·거버넌스 관점으로 분류한 Wiki 문서입니다.',
        '/llm-taxonomy',
        [
            'llm taxonomy',
            'model taxonomy',
            'model classification',
            'latest llm',
            'agentic ai',
            'open weight',
            'multimodal',
            'long context',
            'GPT-5.5',
            'Claude Opus 4.8',
            'Gemini 3.1 Pro',
            'DeepSeek V4',
            'Qwen3-Coder',
            'Mistral Vibe',
            'Llama 4',
            '최신 LLM',
            '분류 체계',
            '모델 분류',
            '에이전트',
        ],
    ),
];

const WIKI_OPERATIONS_ENTRIES: AwesomeLlmAppEntry[] = [
    entry(
        'website-wiki-taxonomy-design',
        '웹사이트 위키 분류체계 설계안',
        '운영·공지 > 편집 정책',
        'Awesome LLM Wiki의 최상위 분류, 하위 카테고리, 문서 배치 기준, 표준 목차, 제목·태그, 검색·유지보수 기준을 정리한 운영 설계 문서입니다.',
        '/wiki-taxonomy-design',
        [
            'wiki taxonomy',
            'content taxonomy',
            'information architecture',
            'category rules',
            'document template',
            'tag rules',
            'archive policy',
            'maintenance',
            '위키 분류체계',
            '콘텐츠 분류',
            '정보구조',
            '문서 목차',
            '태그 규칙',
            '아카이브',
            '운영 기준',
        ],
    ),
];

export const AWESOME_LLM_APP_SECTIONS: AwesomeLlmAppSection[] = [
    {
        id: 'foundations',
        order: '01',
        title: '기초·모델 구조',
        summary: 'LLM 모델을 정리한 섹션입니다.',
        description: 'LLM 모델 구조를 이해하는 데 필요한 핵심 문서를 정리했습니다.',
        related: ['LLM Agent Benchmarks', 'LLM Frameworks', 'Awesome LLM 구축 보고서'],
        entries: [
            entry(
                'transformer',
                'Transformer',
                '딥러닝 아키텍처',
                'Self-attention 기반 LLM 아키텍처의 핵심 구조를 내부 상세 문서로 정리했습니다.',
                '/posts/transformer/view',
                ['transformer', 'self-attention', 'architecture', '트랜스포머', '어텐션', '모델 구조'],
            ),
        ],
        blocks: [],
    },
    {
        id: 'llm-taxonomy',
        order: '02',
        title: 'LLM 분류 체계',
        summary: '최신 모델을 단순 릴리스 순서가 아니라 제공 방식, 능력, 실행 표면, 데이터 처리, 검증 기준으로 나누는 분류 문서입니다.',
        description: '빠르게 바뀌는 LLM 시장을 공급자별 뉴스가 아니라 반복 사용 가능한 분류 체계로 정리합니다. 벤더 수치는 직접 비교하지 않고 어떤 모델군을 어떤 업무와 검증 축에 매핑할지 중심으로 봅니다.',
        related: ['기초·모델 구조', 'LLM Agent Benchmarks', 'Core Codex Skills'],
        entries: TAXONOMY_ENTRIES,
        blocks: [
            { label: '조사 기준', title: '공식 출처 우선', text: '모델 릴리스는 공급자 공식 블로그와 모델 카드, 큰 흐름은 AI Index와 International AI Safety Report를 우선 사용합니다.' },
            { label: '읽는 법', title: '뉴스보다 분류 체계', text: '각 모델을 closed frontier, open-weight, agent product, multimodal, long-context, safety-critical 같은 운영 범주에 배치합니다.' },
        ],
    },
    {
        id: 'benchmarks',
        order: '03',
        title: 'LLM Agent Benchmarks',
        summary: `최신 모델 그래프와 ${BENCHMARK_TOC_ITEMS.length}개 하위 벤치마크 상세 문서를 남긴 성능·검증 분류입니다.`,
        description: '실제 웹사이트 또는 논문 페이지로 별도 확인한 벤치마크만 내부 상세 페이지로 연결합니다. Awesome LLM 목차에서 GitHub 링크를 그대로 가져온 항목은 제외했습니다.',
        related: ['기초·모델 구조', 'LLM Frameworks', 'Core Codex Skills'],
        entries: BENCHMARK_ENTRIES,
        blocks: [
            { label: '조사 범위', title: '벤치마크 허브와 상세 문서', text: '허브에는 최신 모델 그래프를 두고, 개별 벤치마크는 측정 내용, 채점 방식, 활용 기준, 주의점을 상세 페이지에 분리했습니다.' },
            { label: '운영 기준', title: '공개 점수는 방향성 확인용', text: '벤더 발표 점수와 공개 추적 점수는 평가 조건이 다를 수 있어 내부 골든셋과 함께 확인해야 합니다.' },
        ],
    },
    {
        id: 'frameworks',
        order: '04',
        title: 'LLM Frameworks',
        summary: '모델 자체가 아니라 LLM 사용 방식, 에이전트 규칙, 커뮤니케이션 프로토콜을 묶는 프레임워크 분류입니다.',
        description: 'Caveman처럼 특정 런타임에 붙는 단일 스킬보다 넓게, 에이전트가 말하고 기록하고 비용을 줄이는 방식을 규칙 세트로 정의한 항목을 이 분류에 둡니다.',
        related: ['LLM Agent Benchmarks', 'Core Codex Skills', 'Awesome LLM 구축 보고서'],
        entries: FRAMEWORK_ENTRIES,
        blocks: [
            { label: '분류 기준', title: '스킬보다 프레임워크', text: 'Caveman은 개별 Codex 명령이 아니라 Claude Code, Codex, 에이전트 대화 전반에 적용되는 압축 규칙 세트에 가깝습니다.' },
            { label: '효과', title: '커뮤니케이션 비용 절감', text: '응답 형식과 말투를 강하게 제한해 출력 토큰, 상태 보고 길이, 사람이 읽는 부담을 함께 줄이는 데 초점이 있습니다.' },
            { label: '주의', title: '과압축 리스크', text: '문맥 보존과 법무·계약·공식 문서처럼 문장 정확성이 중요한 작업에는 압축 전후 검증이 필요합니다.' },
        ],
    },
    {
        id: 'codex-skills',
        order: '05',
        title: 'Core Codex Skills',
        summary: `${CORE_CODEX_SKILLS.length}개 핵심 Codex 스킬과 /goal 상세 조사만 남긴 실무 스킬 분류입니다.`,
        description: '반복 작업에서 실제로 다시 쓸 가능성이 높은 목표 관리, 조사, 구현, 리뷰 스킬만 유지했습니다.',
        related: ['LLM Agent Benchmarks', 'LLM Frameworks', 'Awesome LLM 구축 보고서'],
        entries: CODEX_SKILL_ENTRIES,
        blocks: [
            { label: '선정 기준', title: '반복성과 검증 가능성', text: '스킬은 입력, 절차, 출력, 검증이 반복 가능한 작업만 남기고 단순 아이디어 수준의 항목은 제외했습니다.' },
            { label: '상세 조사', title: '/goal 상세 문서', text: 'Codex /goal은 별도 상세 페이지로 승격했고, 나머지는 핵심 카탈로그 안에서 운영 기준을 정리했습니다.' },
        ],
    },
    {
        id: 'report',
        order: '06',
        title: 'Awesome LLM 구축 보고서',
        summary: '서비스 범위, 화면 설계, 콘텐츠 체계, 검수 기준을 정리한 내부 보고서입니다.',
        description: '현재 프로젝트에서 조사한 산출물의 범위와 이후 확장 기준을 확인할 수 있는 보고서 문서입니다.',
        related: ['기초·모델 구조', 'LLM Frameworks', 'Core Codex Skills'],
        entries: [
            entry(
                'awesome-llm-report',
                'Awesome LLM 제안서 및 개발 지시사항',
                '보고서',
                '서비스 범위, 화면 설계, 콘텐츠 체계, 데이터 모델 후보, 검수 기준을 정리한 내부 보고서입니다.',
                '/report',
                ['report', 'proposal', 'scope', '보고서', '제안서', '검수'],
            ),
        ],
        blocks: [
            { label: '용도', title: '작업 범위 기준 문서', text: '어떤 문서를 목차에 올릴지, 출처와 변경 이력을 어떻게 관리할지 판단하는 기준 문서입니다.' },
            { label: '후속', title: '미조사 항목은 후속 확장', text: '아직 상세 조사나 내부 문서가 없는 항목은 보고서의 후속 확장 범위로만 남기고 목차에서는 제외합니다.' },
        ],
    },
    {
        id: 'wiki-operations',
        order: '07',
        title: '위키 운영 기준',
        summary: '문서를 어디에 배치하고 어떤 목차·제목·태그·아카이브 기준으로 관리할지 정한 운영 분류입니다.',
        description: '기존 웹사이트 구조를 유지하면서 관리자가 문서를 일관되게 등록하고, 사용자가 같은 기준으로 탐색할 수 있도록 콘텐츠 정보구조와 유지보수 기준을 분리해 둡니다.',
        related: ['Awesome LLM 구축 보고서', 'LLM 분류 체계', 'LLM Agent Benchmarks'],
        entries: WIKI_OPERATIONS_ENTRIES,
        blocks: [
            { label: '배치 기준', title: '주 카테고리는 하나만', text: '하나의 문서는 사용자가 처음 찾을 가능성이 가장 높은 주 카테고리에만 배치하고, 나머지 맥락은 태그와 관련 문서로 연결합니다.' },
            { label: '운영 기준', title: '검색과 아카이브까지 포함', text: '제목, 태그, 요약, 동의어, 정기 검토, 중복·임시·오래된 문서 처리 기준을 함께 관리합니다.' },
        ],
    },
];
